import React, { useEffect, useRef, useState } from 'react';

import { toBirthDate } from '@common/utils/validation';
import { checkValidDate, diseaseTreatInfo } from '@common/utils/index';

import SatQuestionArea from '@components/common/satUI/SatQuestionArea';
import SatCheckBox from '@components/common/satUI/SatCheckBox';
import SatRadio from '@components/common/satUI/SatRadio';
import SatInput from '@components/common/satUI/SatInput';
import SatValidationErrMsg from '@components/common/satUI/SatValidationErrMsg';
import SatModal from '@components/common/satUI/SatModal';
import SatButton from '@components/common/satUI/SatButton';
import SearchDiseasePopupEa from '@components/ea/SearchDiseasePopupEa';

const DiseaseHistory = props => {
  const {
    historyData,
    treatStrtDate,
    treatEndDate,
    index,
    totalLength,
    onChangeCheckBox,
    onDelete,
    addDiseaseHistory,
    showErrorCheckMsg,
    isBeforeRevision,
  } = props;

  const searchInputRef = useRef();
  const inputStartRef = useRef();
  const inputEndRef = useRef();
  const treatContentRef = useRef();
  const hospitalRef = useRef();

  const [treatContent, setTreatContent] = useState('');
  const [hospital, setHospital] = useState('');
  const [searchVisible, setSearchVisible] = useState('');
  const [isDateError, setIsDateError] = useState(false);

  const treatTypeCheckData = [
    { label: '입원', value: 'hospitalization' },
    { label: '수술', value: 'operation' },
    { label: '통원', value: 'outpatient' },
  ];

  const treatAreaCheckData = [
    { label: '좌', value: 'left' },
    { label: '우', value: 'right' },
    { label: '해당없음', value: 'na' },
  ];

  useEffect(() => {
    // 하단 다음버튼 클릭
    if (showErrorCheckMsg) {
      searchInputRef.current.isValidCheck(); // 질병명 input 체크
      treatContentRef.current.isValidCheck(); // 치료내용 input 체크
      hospitalRef.current.isValidCheck(); // 치료병원 input 체크
    }
  }, [showErrorCheckMsg]);

  useEffect(() => {
    setTreatContent(historyData.treatContent);
    setHospital(historyData.hospital);
  }, [historyData]);

  useEffect(() => {
    if (
      (treatStrtDate !== '' &&
        treatEndDate !== '' &&
        treatStrtDate > treatEndDate) ||
      (treatStrtDate !== '' && !checkValidDate(toBirthDate(treatStrtDate))) ||
      (treatEndDate !== '' && !checkValidDate(toBirthDate(treatEndDate)))
    ) {
      setIsDateError(true);
    } else {
      setIsDateError(false);
    }
  }, [treatStrtDate, treatEndDate]);

  const onChangeValue = (type, value) => {
    onChangeCheckBox(index, {
      ...historyData,
      [type]: value,
    });
  };

  const onChangeData = data => {
    onChangeCheckBox(index, {
      ...historyData,
      ...data,
    });
  };

  const onChangeDate = (type, value) => {
    const treatStart = type === 'treatStrtDate' ? value : treatStrtDate;
    const treatEnd = type === 'treatEndDate' ? value : treatEndDate;
    const { treatDayCount } = diseaseTreatInfo(treatStart, treatEnd);

    onChangeData({ [type]: value, treatDayCount });
  };

  // 질병명 선택
  const handleSelectDisease = (data = {}) => {
    const { kcdNm = '', kcdCd = '' } = data;
    const updateData = { diagnosis: kcdNm, kcdNm, kcdCd };

    onChangeData(updateData);
  };

  // 질병 검색 팝업 닫기
  const handleClosePopup = () => {
    setSearchVisible(false);
    searchInputRef.current.onBlur();
    searchInputRef.current.setValidCheckMsg('');
  };

  return (
    <React.Fragment>
      <div className="infor-list">
        {index === 0 && (
          <>
            <h2 className="tit-con">병력정보</h2>
            <SatQuestionArea
              title={() => (
                <p>
                  위의 질문 중 과거 및 현재 질병상태에 `예`를 선택하신 사항이
                  있습니다. 상세 정보를 입력해주세요. (선택하신 10대 질병을
                  포함하여 최대 3개까지 입력하실수있어요.)
                </p>
              )}
            />
          </>
        )}

        <div className="insu-box item-r">
          {totalLength > 1 && (
            <span className="txt-del" onClick={() => onDelete(index)}>
              삭제
            </span>
          )}
          <table className="medi-history">
            <caption>정보선택</caption>
            <colgroup>
              <col style={{ width: '10rem' }} />
              <col />
            </colgroup>
            <tbody>
              <tr id={`diagnosis${index}`}>
                <th scope="row">{index + 1}. 질병명 (증상)</th>
                <td>
                  <SatInput
                    ref={searchInputRef}
                    value={historyData.diagnosis}
                    focusLabel="검색하기"
                    onClick={() => {
                      setSearchVisible(true);
                      searchInputRef.current.onBlur();
                    }}
                    suffix={() => (
                      <SatButton
                        styleClass={`ico ico-search`}
                        onClick={() => setSearchVisible(true)}
                      />
                    )}
                    validRule={[
                      {
                        required: true,
                        errMsg: '질병명(증상)을 입력해 주세요.',
                      },
                    ]}
                  />
                </td>
              </tr>
              <tr id={`treatDate${index}`}>
                <th scope="row">치료기간</th>
                <td>
                  <div className="item-date">
                    <SatInput
                      useDate
                      ref={inputStartRef}
                      value={treatStrtDate}
                      type="number"
                      focusLabel="날짜 (8자리)"
                      maxLength={8}
                      onChange={value => onChangeDate('treatStrtDate', value)}
                    />
                    <span className="bold">~</span>
                    <SatInput
                      useDate
                      ref={inputEndRef}
                      value={treatEndDate}
                      type="number"
                      focusLabel="날짜 (8자리)"
                      maxLength={8}
                      onChange={value => onChangeDate('treatEndDate', value)}
                    />
                  </div>
                  <SatValidationErrMsg
                    visible={
                      isDateError ||
                      (showErrorCheckMsg &&
                        (treatStrtDate === '' || treatEndDate === ''))
                    }
                    type="error"
                    message={
                      isDateError
                        ? '기간을 확인 후 입력해 주세요.'
                        : showErrorCheckMsg &&
                          (treatStrtDate === '' || treatEndDate === '') &&
                          '치료기간을 입력해 주세요.'
                    }
                  />
                </td>
              </tr>
              <tr id={`treatCheck${index}`}>
                <th scope="row">치료내용</th>
                <td>
                  <div className="check-box">
                    {treatTypeCheckData.map(option => {
                      return (
                        <>
                          <SatCheckBox
                            value={[
                              historyData[option.value] === '1' ? true : false,
                            ]}
                            options={[
                              {
                                label: option.label,
                                value: option.value,
                              },
                            ]}
                            onChange={value =>
                              onChangeValue(
                                option.value,
                                value === true ? '1' : '0',
                              )
                            }
                          />
                        </>
                      );
                    })}
                  </div>
                  <SatValidationErrMsg
                    visible={
                      showErrorCheckMsg &&
                      historyData.hospitalization !== '1' &&
                      historyData.operation !== '1' &&
                      historyData.outpatient !== '1'
                    }
                    type="error"
                    message="치료내용을 입력해 주세요."
                  />
                </td>
              </tr>
              <tr id={`treatArea${index}`}>
                <th scope="row">치료내용 (부위)</th>
                <td>
                  <div className="check-box">
                    {treatAreaCheckData.map(option => {
                      return (
                        <>
                          <SatCheckBox
                            value={[
                              historyData.treatArea?.[option.value]
                                ? true
                                : false,
                            ]}
                            options={[
                              {
                                label: option.label,
                                value: option.value,
                              },
                            ]}
                            onChange={value =>
                              option.value === 'na' &&
                              (historyData.treatArea?.left ||
                                historyData.treatArea?.right)
                                ? onChangeValue('treatArea', {
                                    ...historyData.treatArea,
                                    [option.value]: false,
                                  })
                                : option.value !== 'na' &&
                                  historyData.treatArea?.na
                                ? onChangeValue('treatArea', {
                                    ...historyData.treatArea,
                                    [option.value]: false,
                                  })
                                : onChangeValue('treatArea', {
                                    ...historyData.treatArea,
                                    [option.value]: value,
                                  })
                            }
                          />
                        </>
                      );
                    })}
                  </div>
                  <SatValidationErrMsg
                    visible={
                      showErrorCheckMsg &&
                      !historyData.treatArea?.left &&
                      !historyData.treatArea?.right &&
                      !historyData.treatArea?.na
                    }
                    type="error"
                    message="치료내용을 입력해 주세요."
                  />
                </td>
              </tr>
              <tr id={`treatContent${index}`}>
                <th scope="row">치료내용 (상세)</th>
                <td>
                  <SatInput
                    ref={treatContentRef}
                    value={treatContent}
                    type="text"
                    placeholder="입력"
                    onChange={value => {
                      setTreatContent(value);
                      onChangeValue('treatContent', value);  //입력 실시간 감지를 위함
                      // if (value === '') {
                      //   onChangeValue('treatContent', value);
                      // }
                    }}
                    onBlur={() => onChangeValue('treatContent', treatContent)}
                    bold={true}
                    maxLength={32}
                    validRule={[
                      {
                        required: true,
                        errMsg: '치료내용을 입력해 주세요.',
                      },
                    ]}
                  />
                </td>
              </tr>
              {isBeforeRevision && (
                <tr id={`hospital${index}`}>
                  <th scope="row">치료병원</th>
                  <td>
                    <SatInput
                      ref={hospitalRef}
                      value={hospital}
                      type="text"
                      placeholder="입력"
                      onChange={value => {
                        setHospital(value);
                        onChangeValue('hospital', value); //입력 실시간 감지를 위함
                        // if (value === '') {
                        //   onChangeValue('hospital', value);
                        // }
                      }}
                      onBlur={() => onChangeValue('hospital', hospital)}
                      bold={true}
                      maxLength={32}
                      validRule={[
                        {
                          required: true,
                          errMsg: '치료병원을 입력해 주세요.',
                        },
                      ]}
                    />
                  </td>
                </tr>
              )}
              <tr id={`reocur${index}`}>
                <th scope="row">재발경험</th>
                <td>
                  <div className="check-box w70">
                    <SatRadio
                      value={historyData.reocur}
                      options={[
                        { label: '예', value: '1' },
                        { label: '아니오', value: '0' },
                      ]}
                      onChange={value => onChangeValue('reocur', value.value)}
                      name={`reocur${index}`}
                    />
                  </div>
                  <SatValidationErrMsg
                    visible={showErrorCheckMsg && historyData.reocur === ''}
                    type="error"
                    message="재발 경험 여부를 선택해주세요."
                  />
                </td>
              </tr>
              <tr id={`completeCure${index}`}>
                <th scope="row">완치여부</th>
                <td>
                  <div className="check-box">
                    <SatRadio
                      value={historyData.completeCure}
                      options={[
                        { label: '예', value: '1' },
                        { label: '아니오', value: '0' },
                      ]}
                      onChange={value =>
                        onChangeValue('completeCure', value.value)
                      }
                      name={`completeCure${index}`}
                    />
                  </div>
                  <SatValidationErrMsg
                    visible={
                      showErrorCheckMsg && historyData.completeCure === ''
                    }
                    type="error"
                    message="완치여부를 선택해주세요."
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        {index + 1 === totalLength && totalLength < 3 && (
          <div className="box-btns-center mgb-20">
            <SatButton
              styleClass="btn-m b-blue2 w-328"
              iconStyleClass="ico ico-add4"
              label="추가등록"
              onClick={addDiseaseHistory}
            />
          </div>
        )}
      </div>
      <SatModal
        visible={searchVisible}
        setVisible={() => {
          setSearchVisible(false);
          searchInputRef.current.onBlur();
        }}
        type="info"
        wide
      >
        <SearchDiseasePopupEa
          diseaseName={historyData.diagnosis}
          onSelect={handleSelectDisease}
          onClose={handleClosePopup}
        />
      </SatModal>
    </React.Fragment>
  );
};

export default DiseaseHistory;
